import React from "react";

import {
  IColumnsWithValuesProps,
} from "components/dashboard/product-template-table/utils/interfaces";
import {
  arrayToCsv,
  downloadBlob,
  formatDataTotalsAggregation,
  getValueFromTableDataType,
  isInRange,
  s2ab,
} from "components/dashboard/product-template-table/utils/utils";
import { SplitButton } from "primereact/splitbutton";
import { T } from "services/lang/LanguageService";
import { CSV_TYPE } from "utils/constants";
import { CellType } from "utils/enums";
import { TimeSpan, formatDate, timeSpanToSeconds, formatDisplayDatetime } from "utils/time-utils";
import * as XLSX from "xlsx-js-style";
import { ExportDataTableButtonProps } from "./ExportDataTableTypes";
import { useTimezone } from "../../../hooks/useTimezone";

const defaultStyles = {
  border: {
    bottom: { style: "thin", color: { rgb: "777777" } },
    top: { style: "thin", color: { rgb: "777777" } },
    left: { style: "thin", color: { rgb: "777777" } },
    right: { style: "thin", color: { rgb: "777777" } },
  },
  alignment: {
    horizontal: "center",
    vertical: "center",
  },
};

const ExportDataTableButton = ({
  header,
  body,
  tableName,
  transposed = false,
  showTotal = false,
}: ExportDataTableButtonProps) => {
  const tz = useTimezone();
  const totalRow: IColumnsWithValuesProps[] = [];
  if (showTotal) {
    totalRow.push(...formatDataTotalsAggregation(header, body));
  }

  const items = [
    {
      label: "CSV",
      icon: "pi pi-file",
      command: () => exportCSVFile(),
    },
    {
      label: "XLS",
      icon: "pi pi-file-excel",
      command: () => exportExcelFile(),
    },
  ];

  const extractJSONFromProps = () => {
    if (header && body) {
      const templateArr = body.map(obj => {
        return header.data?.attributes.map(ob => {
          const o = Object.values(obj.entries).find(
            entry => entry.referenceName === ob.referenceName
          );
          return {
            name: ob.displayName.replaceAll("<br/>", " "),
            value: getValueFromTableDataType(o),
          };
        });
      });

      if (templateArr != null) {
        return templateArr;
      }
    }
    return new Array<Array<any>>();
  };

  const extractArrayOfArraysFromProps = () => {
    if (header && body) {
      const headers = header.data?.attributes.map(ob => {
        return {
          v: ob.displayName.replaceAll("<br/>", " "),
          t: "s",
          s: {
            ...defaultStyles,
            fill: {
              fgColor: { rgb: "EEEEEE" },
            },
            font: {
              bold: true,
              color: { rgb: "000000" },
            },
          },
        };
      });

      const data = body.map((obj, i) => {
        return header.data?.attributes.map(ob => {
          const o = Object.values(obj.entries).find(
            entry => entry.referenceName === ob.referenceName
          );
          const value =
            ob.type === "INSTANT"
              ? formatDisplayDatetime(o?.value as string, tz.display)
              : getValueFromTableDataType(o);

          const color =
            ob?.bgHighlightRanges?.length! > 0
              ? ob.bgHighlightRanges?.filter(bg =>
                  isInRange(
                    o?.type === CellType.TIMESPAN
                      ? timeSpanToSeconds(o?.value as TimeSpan)
                      : (o?.value as number),
                    bg
                  )
                )[0]
              : ob.highlightColor;

          const defaultBgColor = i % 2 === 0 ? "FFFFFF" : "F9F9F9";
          return {
            v: value,
            t: o?.type === CellType.LONG || o?.type === CellType.DOUBLE ? "n" : "s",
            s: {
              ...defaultStyles,
              fill: { fgColor: { rgb: color?.bgColor?.replace("#", "") ?? defaultBgColor } },
              font: {
                color: { rgb: color?.color?.replace("#", "") ?? "000000" },
              },
            },
          };
        });
      });

      const total = showTotal
        ? totalRow.map(t => {
            return {
              v: t.total ?? "",
              t: t.total ? "n" : "s",
              s: {
                ...defaultStyles,
                fill: { fgColor: { rgb: "EEEEEE" } },
                font: {
                  bold: true,
                  color: { rgb: "000000" },
                },
              },
            };
          })
        : [];

      if (transposed) {
        const aoaTrans = headers?.map((head, i) =>
          showTotal
            ? [head, ...data.map(row => row?.[i]), total[i]]
            : [head, ...data.map(row => row?.[i])]
        );
        return aoaTrans;
      }
      return showTotal ? [headers, ...data, total] : [headers, ...data];
    }

    console.error("ERROR: extractArrayOfArraysFromProps");
    return new Array<Array<any>>();
  };

  const handleExportExcelBinary = () => {
    if (header && body) {
      const arr: any = extractArrayOfArraysFromProps();
      const ws = XLSX.utils.aoa_to_sheet(arr);

      ws["!rows"] = minimalHeight(arr.length);
      ws["!cols"] = fitToColumn(arr);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws);
      return XLSX.write(wb, { bookType: "xlsx", type: "binary", cellStyles: true });
    }
    return null;
  };

  function fitToColumn(arrayOfArray: Array<Array<any>>) {
    return arrayOfArray[0].map((_, i) => ({
      wch: Math.max(
        ...arrayOfArray.map(a2 => (a2[i] && a2[i].v ? a2[i].v.toString().length + 1 : 0))
      ),
    }));
  }

  function minimalHeight(rowsNumber: number) {
    const arr = [];
    for (let i = 0; i < rowsNumber; i++) {
      arr.push({ hpt: 16 });
    }

    return arr;
  }

  const exportCSVFile = () => {
    downloadBlob(arrayToCsv(extractJSONFromProps(), transposed), `${tableName}.csv`, CSV_TYPE);
  };

  const exportExcelFile = () => {
    downloadBlob(s2ab(handleExportExcelBinary()), `${tableName}.xlsx`, "application/octet-stream");
  };

  return (
    <SplitButton
      label={T("action.export")}
      icon="pi pi-file-export"
      className="p-button-sm p-button-success"
      onClick={exportExcelFile}
      model={items}
      disabled={!header || !body}
    />
  );
};

export default ExportDataTableButton;
